// CN-8874 (Lane Strength): carrier-depth adequacy relative to the customer's declared expected
// volume. A fixed count of carriers is "deep" for a low-volume lane and "thin" for a high-volume
// one, so when a Bulk Rating lane carries a load_count (+ recognized cadence) we scale the depth
// requirement to the volume instead of the flat CN-8872 absolute bands. When no volume (or no
// recognized cadence) is declared — the common case — callers fall back to the absolute bands
// unchanged, so there is no regression for volume-less files.
//
// Pure functions only (no Angular) so they're trivially unit-testable and reusable by the Lane
// Confidence score (CN-8911). Design + tuning notes: docs/plans/LANE_STRENGTH_SCORING_PLAN.md §4a.

// Declared cadence → loads-per-week multiplier. A per-day count is 7×/week; a per-month / quarter /
// year count is a fraction of a week (12 months, 4 quarters, 1 year spread over 52 weeks).
const LOADS_PER_WEEK_BY_RANGE: Record<string, number> = {
	day: 7,
	week: 1,
	month: 12 / 52,
	quarter: 1 / 13, // 4 quarters / 52 weeks
	year: 1 / 52,
};

// required_depth = BASE + loads_per_week / PER_CARRIER, clamped to the meter's [3, 15] range.
// BASE is the floor of carriers we always want on a lane; PER_CARRIER is roughly how many
// loads/week one carrier can absorb. Starting values — to be calibrated against real lanes (§7).
export const DEPTH_BASE = 3;
export const DEPTH_PER_CARRIER = 4;
export const DEPTH_REQUIRED_MIN = 3;
export const DEPTH_REQUIRED_MAX = 15;

// Adequacy → A/B/C: ≥1.0 fully covered (Controlled), 0.5–1.0 competitive, <0.5 exposed.
export type DepthBand = 'A' | 'B' | 'C';

export const DEPTH_BAND_LABEL: Record<DepthBand, string> = {
	A: 'Controlled',
	B: 'Competitive',
	C: 'Exposed',
};

function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

/**
 * Coerce a value the DTO index signature may deliver as a numeric string to a finite number, or null
 * when it's absent / blank / non-numeric. Distinguishes "" or whitespace (missing → null) from "0"
 * (a real zero) — a bare `Number()` collapses both to 0 and so fabricates a worst-case reading for
 * fields (carrier_count, all_in, …) that are simply unset. Used everywhere a raw field is read.
 */
export function coerceNumber(value: number | string | null | undefined): number | null {
	if (value == null) {
		return null;
	}

	if (typeof value === 'string') {
		const trimmed = value.trim();

		if (trimmed === '') {
			return null;
		}

		const parsed = Number(trimmed);

		return Number.isFinite(parsed) ? parsed : null;
	}

	return Number.isFinite(value) ? value : null;
}

/**
 * Declared count + cadence → loads/week, or null when there's no usable count or no recognized
 * cadence. A bare count with an unknown/blank cadence returns null on purpose: we only
 * volume-adjust when the basis is known, so an unlabeled count never silently reshapes the score.
 */
export function loadsPerWeek(
	loadCount: number | null | undefined,
	range: string | null | undefined,
): number | null {
	const count = coerceNumber(loadCount);

	if (count == null || count <= 0) {
		return null;
	}

	const key = typeof range === 'string' ? range.toLowerCase().trim() : '';

	// Own-property lookup only: a raw cadence equal to an Object.prototype key ("constructor",
	// "toString", …) must not resolve to a function and multiply into a NaN score.
	if (!Object.prototype.hasOwnProperty.call(LOADS_PER_WEEK_BY_RANGE, key)) {
		return null;
	}

	return count * LOADS_PER_WEEK_BY_RANGE[key];
}

/** required_depth from an already-computed loads/week (shared by requiredDepth + assessDepth). */
function requiredDepthFor(perWeek: number): number {
	return clamp(DEPTH_BASE + perWeek / DEPTH_PER_CARRIER, DEPTH_REQUIRED_MIN, DEPTH_REQUIRED_MAX);
}

/** Carriers we'd want on the lane for the declared volume, or null when no volume is declared. */
export function requiredDepth(
	loadCount: number | null | undefined,
	range: string | null | undefined,
): number | null {
	const perWeek = loadsPerWeek(loadCount, range);

	return perWeek == null ? null : requiredDepthFor(perWeek);
}

/** The full volume-adjusted depth read in one pass. */
export interface DepthAssessment {
	perWeek: number;
	required: number;
	adequacy: number;
	band: DepthBand;
}

/**
 * One-pass volume-adjusted depth assessment (loads/week → required → adequacy → band), or null
 * when there's no recognized volume or no carrier count. Single source of truth for both the color
 * and the tooltip, so the display path doesn't recompute the loads/week chain several times per
 * change-detection cycle. carrier_count is coerced like the count (numeric-string tolerant).
 */
export function assessDepth(
	carrierCount: number | null | undefined,
	loadCount: number | null | undefined,
	range: string | null | undefined,
): DepthAssessment | null {
	const perWeek = loadsPerWeek(loadCount, range);
	const carriers = coerceNumber(carrierCount);

	if (perWeek == null || carriers == null) {
		return null;
	}

	const required = requiredDepthFor(perWeek);
	const adequacy = clamp(carriers / required, 0, 1);

	return { perWeek, required, adequacy, band: depthBand(adequacy) as DepthBand };
}

/** carrier_count / required_depth, clamped to [0, 1]; null when depth or volume is unknown. */
export function depthAdequacy(
	carrierCount: number | null | undefined,
	loadCount: number | null | undefined,
	range: string | null | undefined,
): number | null {
	return assessDepth(carrierCount, loadCount, range)?.adequacy ?? null;
}

/** Adequacy → A/B/C band; null when adequacy is unknown. */
export function depthBand(adequacy: number | null): DepthBand | null {
	if (adequacy == null) {
		return null;
	}

	if (adequacy >= 1) {
		return 'A';
	}

	return adequacy >= 0.5 ? 'B' : 'C';
}

/** A/B/C → the shared depth color class (matches carrierDepthColorClass' red→yellow→green). */
export function depthBandColorClass(band: DepthBand | null): string {
	switch (band) {
		case 'A':
			return 'depth-high';

		case 'B':
			return 'depth-mid';

		case 'C':
			return 'depth-low';

		default:
			return '';
	}
}

// ── CN-8911: Lane Confidence composite ───────────────────────────────────────────────────────
// Lane Confidence blends three 0–1 axes into a 0–5 score: depth adequacy, rate reliability, and
// the rate-model confidence. Design: docs/plans/LANE_STRENGTH_SCORING_PLAN.md §4b–§4d.

// The CN-8872 "deep/green" carrier count. Used to score depth on a 0–1 scale for lanes with NO
// declared volume (carrier_count / target). Keep in sync with CARRIER_DEPTH_METER.high.
// No-volume absolute depth band boundaries — the SINGLE SOURCE for the Carriers column too:
// CARRIER_DEPTH_METER.low/high in bulk-rating-list-metrics.ts is built from these, so the Capacity
// chip's band, the Carrier-count column's color, and the depth gate can't drift apart. (list-metrics
// imports these; lane-strength imports nothing from it, so there's no cycle.)
export const DEPTH_ABSOLUTE_TARGET = 8; // ≥ → Controlled (A)
export const DEPTH_ABSOLUTE_FLOOR = 3; //  ≥ → Competitive (B); below → Exposed (C)

// Observed cost spread (as a fraction of the predicted all-in) at which rate reliability hits 0.
// A ±25%-of-all-in band ≈ 0.5 spread → fully unreliable. Tunable (§7 calibration).
export const DISPERSION_FULL = 0.5;

// Depth GOVERNS the ceiling; rate + confidence only modulate a lane within [QUALITY_FLOOR, 1] of
// what its depth supports. So an Exposed-depth lane (adequacy < 0.5) can never reach the green band
// no matter how reliable the rate or how high the confidence (§4d). Tunable.
export const QUALITY_WEIGHTS = { rate: 0.6, confidence: 0.4 };
export const QUALITY_FLOOR = 0.6;

/**
 * No-volume carrier count → 0–1, banded to match the CN-8872 Carrier-count column: 0 at 0 carriers,
 * 0.5 at FLOOR, 1.0 at TARGET. So depthBand(score) is C below FLOOR, B in [FLOOR, TARGET), A at/above
 * TARGET — identical to the count column's red/amber/green.
 */
function absoluteDepthScore(carriers: number): number {
	if (carriers >= DEPTH_ABSOLUTE_TARGET) {
		return 1;
	}

	if (carriers >= DEPTH_ABSOLUTE_FLOOR) {
		return 0.5 + (0.5 * (carriers - DEPTH_ABSOLUTE_FLOOR)) / (DEPTH_ABSOLUTE_TARGET - DEPTH_ABSOLUTE_FLOOR);
	}

	return clamp((0.5 * carriers) / DEPTH_ABSOLUTE_FLOOR, 0, 0.5);
}

/**
 * Depth as a 0–1 score for the Lane Confidence blend AND its Capacity chip. Volume-adjusted adequacy
 * when the lane declares a recognized volume; otherwise the band-matched absolute posture. null when
 * there's no carrier count at all.
 */
export function depthScore(
	carrierCount: number | null | undefined,
	loadCount: number | null | undefined,
	range: string | null | undefined,
): number | null {
	const adequacy = depthAdequacy(carrierCount, loadCount, range);

	if (adequacy != null) {
		return adequacy;
	}

	const carriers = coerceNumber(carrierCount);

	return carriers == null ? null : absoluteDepthScore(carriers);
}

/**
 * v1 rate-reliability proxy (fills the rate-position slot until the v2 margin cushion lands): a
 * tight observed cost spread with the prediction inside it → reliable (near 1); a wide spread, or
 * a prediction outside the observed range, → unreliable (toward 0). null when the inputs are absent.
 */
export function rateReliability(
	allIn: number | null | undefined,
	minCost: number | null | undefined,
	maxCost: number | null | undefined,
): number | null {
	const predicted = coerceNumber(allIn);
	const min = coerceNumber(minCost);
	const max = coerceNumber(maxCost);

	if (predicted == null || predicted <= 0 || min == null || max == null) {
		return null;
	}

	const dispersion = Math.max(0, max - min) / predicted;
	let reliability = clamp(1 - dispersion / DISPERSION_FULL, 0, 1);

	// The prediction sitting outside the observed paid range is itself a reliability hit.
	if (predicted < min || predicted > max) {
		reliability = Math.min(reliability, 0.5);
	}

	return reliability;
}

/**
 * Rate reliability + confidence → a 0–1 "quality", renormalized over whichever are present, or NULL
 * when neither is known. Null propagates up to laneStrength → laneCell dashes, so a lane with no
 * rate and no confidence is NOT scored off depth alone (the unpriced-but-deep-lane guard).
 */
function axisQuality(rate: number | null, confidence: number | null): number | null {
	const terms = [
		{ weight: QUALITY_WEIGHTS.rate, value: rate },
		{ weight: QUALITY_WEIGHTS.confidence, value: confidence },
	].filter((term) => term.value != null && Number.isFinite(term.value));

	if (!terms.length) {
		return null;
	}

	const totalWeight = terms.reduce((sum, term) => sum + term.weight, 0);
	const weighted = terms.reduce((sum, term) => sum + term.weight * (term.value as number), 0);

	return clamp(weighted / totalWeight, 0, 1);
}

/**
 * 0–1 lane strength — DEPTH-GOVERNED: depth sets the ceiling and rate + confidence modulate a lane
 * within [QUALITY_FLOOR, 1] of it. So Exposed depth (adequacy < 0.5) tops out below the green band
 * regardless of rate/confidence. null when there's no depth signal (a coverability score with no
 * depth is meaningless).
 */
export function laneStrength(
	depth: number | null,
	rate: number | null,
	confidence: number | null,
): number | null {
	if (depth == null || !Number.isFinite(depth)) {
		return null;
	}

	// Require a quality signal: with neither rate nor confidence there's nothing to assess the lane
	// against, so it must NOT read strong off depth alone (e.g. an unpriced-but-deep lane → dash).
	const quality = axisQuality(rate, confidence);

	if (quality == null) {
		return null;
	}

	return clamp(depth * (QUALITY_FLOOR + (1 - QUALITY_FLOOR) * quality), 0, 1);
}

/** 0–1 lane strength → the 0–5 Lane Confidence score (reuses the confidence meter scale). */
export function laneConfidenceScore(strength: number | null): number | null {
	return strength == null ? null : strength * 5;
}

// Lane Confidence meter (0–5) Green/Yellow/Red thresholds. LANE_CONFIDENCE_METER
// (bulk-rating-list-metrics) is built from these. The meter shows the OVERALL score; the separate
// Capacity chip shows the DEPTH band (depthBand) — two distinct signals, kept from contradicting by
// the depth gate (Exposed depth can't produce a green meter), not by sharing thresholds.
export const LANE_CONFIDENCE_GREEN = 3.5; // ≥ → green
export const LANE_CONFIDENCE_AMBER = 2; //   ≥ → amber; below → red
